import { useEffect, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import CardFrom, { CardFormChildrenConfig, CardFormConfig } from '../../component/card/card-from';
import CommonInput from '../../component/form/common-input.component';
import ButtonComponent from '../../component/button/button.component';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import { Admin } from '../../models';
import apiManage from '../../request';
import {
    normalizePixabayApiKeysForUi,
    preparePixabayApiKeysForSubmit,
} from '../../utils/pixabay-api-keys';

/** 表单中的单行 Key（含稳定 React key，避免删行错位） */
type PixabayKeyRow = {
    /** 行唯一 id（仅前端） */
    id: string;
    /** 输入框中的 API Key 文案 */
    value: string;
};

/**
 * 超管端 Pixabay 搜图 Key 设置页。
 * GET/PATCH 与 Fiskaly 设置相同：`get_admin_id_id` / `patch_admin_id_admin`。
 */
export default function PixabaySettingAdmin() {
    /** 原始 Admin 信息（保留 _id 等） */
    const [userInfo, setUserInfo] = useState<Admin>({});
    /** 表单中的 key 行（至少一行，可含空串） */
    const [editRows, setEditRows] = useState<PixabayKeyRow[]>([
        { id: 'pixabay-row-0', value: '' },
    ]);
    /** 是否已成功拉取 Admin（未加载完成禁止保存，避免误提交空数组清空线上 Key） */
    const [loaded, setLoaded] = useState(false);
    /** 行 id 自增种子 */
    const rowIdSeedRef = useRef(1);

    useEffect(() => {
        getUserInfo();
    }, []);

    /**
     * 为新行生成稳定 id。
     * @returns 形如 `pixabay-row-n` 的唯一 id
     */
    const nextRowId = (): string => {
        const id = `pixabay-row-${rowIdSeedRef.current}`;
        rowIdSeedRef.current += 1;
        return id;
    };

    /**
     * 将字符串数组转为带稳定 id 的行。
     * @param keys - 规范化后的 UI 字符串列表
     */
    const keysToRows = (keys: string[]): PixabayKeyRow[] => {
        return keys.map((value) => ({
            id: nextRowId(),
            value,
        }));
    };

    /** 读取 Admin，回填 `pixabay_api_keys` 到动态表单 */
    const getUserInfo = async () => {
        setLoaded(false);
        const account = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT) || {};
        if (!account._id) {
            GlobalNotification.error(`${i18n.t('dish_book_msg_save_failed')}`);
            return;
        }
        const info = await apiManage.get_admin_id_id(account._id || '');
        if (!info?.error && info?._id) {
            setUserInfo(info);
            // 重置种子，保证本次回填行 id 从 0 起可预期（仅会话内唯一即可）
            rowIdSeedRef.current = 0;
            setEditRows(keysToRows(normalizePixabayApiKeysForUi(info.pixabay_api_keys)));
            setLoaded(true);
            return;
        }
        GlobalNotification.error(`${i18n.t('dish_book_msg_save_failed')}`);
    };

    /**
     * 更新某一行 key 文案。
     * @param rowId - 行 id
     * @param value - 输入值
     */
    const onKeyChange = (rowId: string, value: string) => {
        setEditRows((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, value } : row))
        );
    };

    /** 在末尾新增一行空输入 */
    const onAddKey = () => {
        setEditRows((prev) => [...prev, { id: nextRowId(), value: '' }]);
    };

    /**
     * 删除指定行；至少保留一行；首行不可删。
     * @param rowId - 要删除的行 id
     * @param index - 行下标（仅 index >= 1 可删）
     */
    const onRemoveKey = (rowId: string, index: number) => {
        setEditRows((prev) => {
            if (prev.length <= 1 || index < 1) {
                return prev;
            }
            return prev.filter((row) => row.id !== rowId);
        });
    };

    /**
     * 保存：去重校验通过后 PATCH `pixabay_api_keys`。
     * 全部为空时传 `[]`；未加载成功时不请求。
     */
    const onFormSave = async () => {
        if (!loaded || !userInfo._id) {
            return;
        }
        const prepared = preparePixabayApiKeysForSubmit(
            editRows.map((row) => row.value)
        );
        if (!prepared.ok) {
            GlobalNotification.error(i18n.t('pixabay_setting_page_alert_duplicate_key'));
            return;
        }
        const newAccount: Admin = {
            _id: userInfo._id,
            pixabay_api_keys: prepared.keys,
        };
        const result = await apiManage.patch_admin_id_admin(newAccount);
        if (result && !result?.error) {
            GlobalNotification.success(`${i18n.t('dish_book_msg_save_success')}`);
            getUserInfo();
        } else {
            GlobalNotification.error(`${i18n.t('dish_book_msg_save_failed')}`);
        }
    };

    /**
     * 动态 key 列表：每行右侧预留删除按钮位，首行 visibility:hidden 占位，
     * 保证有/无删除图标时输入框宽度对齐；删除图标复用 ButtonComponent DELETE（DeleteForever）。
     */
    const keysListChildren = (
        <Stack spacing={2} sx={{ width: '100%' }}>
            {editRows.map((row, index) => {
                // 是否为可删除行（第二行起）
                const canDelete = index >= 1;
                return (
                    <Box
                        key={row.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <CommonInput
                                inputType='INPUT'
                                fullWidth
                                label={i18n.t('pixabay_setting_page_label_api_key')}
                                value={row.value}
                                onChange={(e) => onKeyChange(row.id, e.target.value)}
                            />
                        </Box>
                        {/* 固定占位：首行隐藏但仍占宽，与可删行输入框右缘对齐 */}
                        <Box
                            sx={{
                                flexShrink: 0,
                                visibility: canDelete ? 'visible' : 'hidden',
                                pointerEvents: canDelete ? 'auto' : 'none',
                                marginLeft: 2,
                            }}
                            aria-hidden={!canDelete}
                        >
                            <ButtonComponent
                                action='DELETE'
                                color='secondary'
                                handleClick={() => onRemoveKey(row.id, index)}
                                disabled={!canDelete || !loaded}
                                space={false}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Stack>
    );

    /** 卡片子项：自定义动态表单区域 */
    const formData: CardFormChildrenConfig[] = [
        {
            key: 'pixabay_api_keys',
            comType: 'CUSTOMCHILDREN',
            xs: 12,
            md: 12,
            customSubChildren: keysListChildren,
        },
    ];

    /** 卡片配置：标题旁保存 + 新增 */
    const cardConfig: CardFormConfig[] = [
        {
            md: 12,
            columnSpacing: { xs: 2 },
            rowSpacing: 4,
            key: 'base_setting_title',
            title: i18n.t('base_setting_title'),
            actionRender: (
                <>
                    <ButtonComponent
                        action='ADD'
                        handleClick={() => onAddKey()}
                        disabled={!loaded}
                    />
                    <ButtonComponent
                        action='SAVE'
                        handleClick={() => onFormSave()}
                        async={true}
                        disabled={!loaded}
                    />

                </>
            ),
            children: formData,
        },
    ];

    return (
        <Box sx={{ maxWidth: 600 }}>
            <CardFrom config={cardConfig} data={{}} />
        </Box>
    );
}
